import { useState } from 'react';

import { ApiError } from '../../shared/api/errors';
import { Button } from '../../shared/ui/button';
import { ConfirmDelete } from '../../shared/ui/confirm-delete';
import { PlusIcon } from '../../shared/ui/icons';
import { MutationError } from '../../shared/ui/mutation-error';
import { PanelEmpty, SectionPanel } from '../../shared/ui/section-panel';
import { ErrorState, LoadingState } from '../../shared/ui/states';
import { Table } from '../../shared/ui/table';
import {
  appOf,
  inUseSteps,
  secretWordOf,
  stepCount,
  usageSteps,
  type ICredentialStep,
  type TCredential,
  type TCredentialCheck,
  type TCredentialSaved,
} from '../../entities/credential';
import { AddCredentialsDialog } from './add-credentials-dialog';
import { CheckFailedDialog } from './check-failed-dialog';
import { credentialColumns } from './credential-row';
import { CredentialStepsDialog } from './credential-steps-dialog';
import { ReplaceSecretDialog } from './replace-secret-dialog';
import type { TCredentials } from './use-credentials';

type TDialog =
  | { kind: 'failed'; credential: TCredential; check: TCredentialCheck }
  | { kind: 'replace'; id: string }
  | { kind: 'used'; id: string }
  | { kind: 'refused'; id: string; steps: ICredentialStep[] }
  | { kind: 'remove'; id: string };

const replaceLabel = (credential: TCredential): string | null => {
  const app = appOf(credential);
  return app ? `Replace the ${secretWordOf(app, credential.variant)}` : null;
};

interface ICredentialsPanelProps {
  credentials: TCredentials;
  editable: boolean;
  adding: boolean;
  onAddingChange: (open: boolean) => void;
  timeZone?: string;
}

export const CredentialsPanel = ({
  credentials,
  editable,
  adding,
  onAddingChange,
  timeZone,
}: ICredentialsPanelProps) => {
  const { list, connectors, check, remove } = credentials;
  const [dialog, setDialog] = useState<TDialog | null>(null);
  const now = Date.now();
  const rows = list.data ?? [];
  const catalog = connectors.data ?? [];
  const find = (id: string) => rows.find((row) => row.id === id);
  const close = () => setDialog(null);

  const showFailed = (saved: TCredentialSaved) => {
    if (saved.check && !saved.check.ok) {
      setDialog({ kind: 'failed', credential: saved.credential, check: saved.check });
    }
  };

  const runCheck = (id: string) =>
    check.mutate(id, {
      onSuccess: (saved) => {
        if (saved.check?.ok) {
          setDialog((current) => (current?.kind === 'failed' ? null : current));
        } else showFailed(saved);
      },
    });

  const askRemove = (credential: TCredential) =>
    credential.usage.steps > 0
      ? setDialog({ kind: 'refused', id: credential.id, steps: usageSteps(credential.usage) })
      : setDialog({ kind: 'remove', id: credential.id });

  if (list.isError) {
    return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  }
  if (connectors.isError) {
    return <ErrorState error={connectors.error} onRetry={() => void connectors.refetch()} />;
  }
  if (list.isPending || connectors.isPending) return <LoadingState rows={6} />;

  const current = dialog && 'id' in dialog ? find(dialog.id) : undefined;
  const currentApp = current ? appOf(current) : null;

  const failing = rows.filter((row) => row.lastCheckOk === false).length;

  return (
    <>
      <SectionPanel
        title="Saved credentials"
        note={`${rows.length} saved${failing > 0 ? ` · ${failing} failing their check` : ''}${editable ? '' : ' · only owners and admins can change them'}`}
      >
        {dialog?.kind !== 'failed' && check.isError && (
          <div className="px-card py-3">
            <MutationError error={check.error} />
          </div>
        )}
        {rows.length === 0 ? (
          <PanelEmpty
            title="No credentials yet"
            action={
              editable && (
                <Button variant="secondary" size="sm" onClick={() => onAddingChange(true)}>
                  <PlusIcon size={12} /> Add credentials
                </Button>
              )
            }
          >
            Add the API key or login an AI, Slack, Telegram, email or HTTP step needs, then pick it
            in that step. Once saved, a secret can’t be read back — only replaced.
          </PanelEmpty>
        ) : (
          <Table
            tall
            layoutId="settings-credentials"
            columns={credentialColumns({
              connectors: catalog,
              editable,
              checkingId: check.isPending ? (check.variables ?? null) : null,
              now,
              timeZone,
              onCheck: (credential) => runCheck(credential.id),
              onReplace: (credential) => setDialog({ kind: 'replace', id: credential.id }),
              onUsed: (credential) => setDialog({ kind: 'used', id: credential.id }),
              onRemove: askRemove,
            })}
            rows={rows}
            rowKey={(credential) => credential.id}
          />
        )}
      </SectionPanel>

      <AddCredentialsDialog
        open={adding}
        onOpenChange={onAddingChange}
        connectors={catalog}
        onFailed={showFailed}
      />

      {dialog?.kind === 'failed' && (
        <CheckFailedDialog
          credential={find(dialog.credential.id) ?? dialog.credential}
          check={dialog.check}
          timeZone={timeZone}
          editable={editable}
          replaceLabel={replaceLabel(dialog.credential)}
          checking={check.isPending}
          checkError={check.error}
          onReplace={() => setDialog({ kind: 'replace', id: dialog.credential.id })}
          onCheckAgain={() => runCheck(dialog.credential.id)}
          onOpenChange={(open) => !open && close()}
        />
      )}

      {dialog?.kind === 'replace' && current && currentApp && (
        <ReplaceSecretDialog
          credential={current}
          app={currentApp}
          onOpenChange={(open) => !open && close()}
          onFailed={showFailed}
        />
      )}

      {dialog?.kind === 'used' && current && (
        <CredentialStepsDialog
          title={`Where ${current.name} is used`}
          description={
            current.usage.steps === 0
              ? 'Nothing uses it yet: no published version and no draft names it.'
              : `${stepCount(current.usage.steps)} in published versions and drafts.`
          }
          steps={usageSteps(current.usage)}
          app={currentApp?.app ?? null}
          onOpenChange={(open) => !open && close()}
        />
      )}

      {dialog?.kind === 'refused' && (
        <CredentialStepsDialog
          title={`${current?.name ?? 'These credentials'} can’t be deleted`}
          description={`${stepCount(dialog.steps.length)} use${dialog.steps.length === 1 ? 's' : ''} it. Give them other credentials first, or delete the steps.`}
          steps={dialog.steps}
          app={currentApp?.app ?? null}
          onOpenChange={(open) => !open && close()}
        />
      )}

      <ConfirmDelete
        open={dialog?.kind === 'remove' && current !== undefined}
        onOpenChange={(open) => !open && close()}
        entity="credentials"
        verb="Remove"
        name={dialog?.kind === 'remove' ? (current?.name ?? '') : ''}
        note={remove.isError ? <MutationError error={remove.error} /> : undefined}
        waiting={remove.isPending}
        onConfirm={() => {
          if (dialog?.kind !== 'remove') return;
          const id = dialog.id;
          remove.mutate(id, {
            onSuccess: close,
            onError: (error) => {
              // Something named them between the list and the click: the server says which.
              if (error instanceof ApiError && error.code === 'credentials.in_use') {
                setDialog({ kind: 'refused', id, steps: inUseSteps(error.rawDetails) });
              }
            },
          });
        }}
      />
    </>
  );
};
